import { CButton } from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilBook, cilCalendarCheck, cilChartLine, cilWarning } from '@coreui/icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { consoleApi, apiErrorMessage } from '../../api/client'
import { ErrorState, LoadingState } from '../../components/common/AsyncState'
import { MetricCard } from '../../components/common/MetricCard'
import { PageHeader } from '../../components/common/PageHeader'
import { MachineStateCard } from '../../components/public/MachineStateCard'
import { formatDateTime } from '../../utils/format'

export function ConsoleDashboardPage() {
  const query = useQuery({
    queryKey: ['console', 'dashboard'],
    queryFn: consoleApi.dashboard,
    refetchInterval: 60_000,
  })
  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data)
    return <ErrorState message={apiErrorMessage(query.error)} retry={() => void query.refetch()} />
  const data = query.data
  const overview = {
    generatedAt: new Date().toISOString(),
    demoMode: false,
    machine: data.machine,
    current: data.current,
    summary: {
      observedHours: 0,
      availabilityPct: 0,
      averageGpuUtilizationPct: 0,
      averageMemoryUtilizationPct: 0,
      computeEquivalentGpuHours: 0,
      activeProjectCount: 0,
      researchOutputCount: 0,
    },
    series: [],
    stateBreakdown: [],
    recentLedger: [],
    featuredResearch: [],
    services: [],
    upcomingWorkloads: [],
  }
  return (
    <>
      <PageHeader
        eyebrow="Authenticated operations layer"
        title={`Welcome, ${data.user.displayName}`}
        description="Current machine condition, records requiring attention and your next scheduled work."
        actions={
          <CButton color="primary" as={Link} to="/console/drafts">
            Create audit record
          </CButton>
        }
      />
      <MachineStateCard overview={overview} />
      <div className="metric-grid metric-grid--four mt-4">
        <MetricCard
          label="Submitted audit drafts"
          value={data.counts.submittedDrafts}
          helper="Awaiting independent review"
          icon={<CIcon icon={cilBook} />}
          emphasis
        />
        <MetricCard
          label="Active workloads"
          value={data.counts.activeWorkloads}
          helper="Queued or running"
          icon={<CIcon icon={cilChartLine} />}
        />
        <MetricCard
          label="Open incidents"
          value={data.counts.openIncidents}
          helper="Not resolved or reviewed"
          icon={<CIcon icon={cilWarning} />}
        />
        <MetricCard
          label="Upcoming maintenance"
          value={data.counts.upcomingMaintenance}
          helper="Planned preservation actions"
          icon={<CIcon icon={cilCalendarCheck} />}
        />
      </div>
      <div className="console-two-column mt-4">
        <article className="panel">
          <div className="panel__header">
            <div>
              <span className="eyebrow">Audit workflow</span>
              <h2>Recent drafts</h2>
            </div>
            <Link to="/console/drafts">Open all</Link>
          </div>
          <div className="compact-list">
            {data.recentDrafts.length ? (
              data.recentDrafts.map((item) => (
                <div key={item._id}>
                  <span className={`draft-status draft-${item.status}`}>{item.status}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.category} · updated {formatDateTime(item.updatedAt)}
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <p>No audit drafts are assigned to this view.</p>
            )}
          </div>
        </article>
        <article className="panel">
          <div className="panel__header">
            <div>
              <span className="eyebrow">Schedule</span>
              <h2>Upcoming workloads</h2>
            </div>
            <Link to="/console/workloads">Open schedule</Link>
          </div>
          <div className="compact-list">
            {data.upcomingWorkloads.length ? (
              data.upcomingWorkloads.map((item) => (
                <div key={item._id}>
                  <span className={`workload-status workload-${item.status}`}>{item.status}</span>
                  <div>
                    <strong>{item.title}</strong>
                    <small>
                      {item.projectAlias} · {formatDateTime(item.startsAt)}
                    </small>
                  </div>
                </div>
              ))
            ) : (
              <p>No upcoming workload is assigned.</p>
            )}
          </div>
        </article>
      </div>
    </>
  )
}
